import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import {
  isActiveEvidenceRunStatus,
  isSstv1LockerId,
  SSTV2_EVIDENCE_DEFAULT_RESERVATION_COUNT,
  SSTV2_EVIDENCE_FIXTURE_TENANT,
  Sstv2EvidenceSeedPlan,
  Sstv2MigrationEvidenceRunThingTypeMrn,
} from "shared/sstv2MigrationEvidenceData"
import { Sstv2MigrationEvidenceRun } from "shared/sstv2MigrationEvidenceRun"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import {
  useCancelSstv2EvidenceRunMutation,
  useStartSstv2EvidenceRunMutation,
} from "src/features/sstv2-evidence/sstv2EvidenceApi"
import { selectMeIsReady } from "src/features/me/meSlice"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { useGetTenantsQuery } from "src/features/things/thingsSaApi"
import { selectSaList, thingsActions } from "src/features/things/thingsSlice"
import Sstv2EvidenceRunControls from "./Sstv2EvidenceRunControls"
import Sstv2EvidenceRunHistory from "./Sstv2EvidenceRunHistory"
import { evidenceStatusColor } from "./evidenceStatusColor"
import Sstv2EvidenceSafetyBanner from "./Sstv2EvidenceSafetyBanner"
import Sstv2EvidenceRunResult from "./Sstv2EvidenceRunResult"
import Sstv2EvidenceRunStepper from "./Sstv2EvidenceRunStepper"

const POLL_INTERVAL_MS = 8000

/**
 * Where the operator's SL1 locker id is remembered. `localStorage`, not a DDB
 * setting: it is an operator's working context (which environment am I pointed
 * at right now), not tenant configuration, so it must not become a deployed
 * artefact. Accepted consequence — a new browser means pasting it again.
 */
const SOURCE_LOCKER_STORAGE_KEY = "sl2.sstv2Evidence.sourceLocker"

/** Last-used locker id, or "" — never a constant, so an empty box stays empty. */
const readRememberedSourceLocker = (): string => {
  try {
    const v = window.localStorage.getItem(SOURCE_LOCKER_STORAGE_KEY) ?? ""
    return isSstv1LockerId(v) ? v.trim() : ""
  } catch {
    return "" // storage disabled/blocked — the operator just pastes it again
  }
}

/**
 * "Synthetic Migration" tab — trigger a backend-driven V1→V2 migration +
 * rollback evidence run, watch its 6 steps live, and read the completeness
 * verdict. The FE only displays; the orchestrator Lambda does the work.
 */
export const Sstv2SyntheticMigrationTab: FC = () => {
  const { t } = useTranslation()
  const isReady = useAppSelector(selectMeIsReady)
  const dispatch = useAppDispatch()
  const [reseed, setReseed] = useState(true)
  const [runRollback, setRunRollback] = useState(true)
  const [targetTenantId, setTargetTenantId] = useState<string>(
    SSTV2_EVIDENCE_FIXTURE_TENANT,
  )
  // Operator-supplied, prefilled from the last value this browser used. There is
  // deliberately no fallback constant: an empty box is the correct state on a
  // fresh browser, and the run cannot start until it is filled (LOCKER-344).
  const [sourceLocker, setSourceLocker] = useState<string>(
    readRememberedSourceLocker,
  )
  // Reseed sizing: a plain total, or (when the operator opts in) exact per-status
  // counts that override it. null = no override.
  const [reservationCount, setReservationCount] = useState<number>(
    SSTV2_EVIDENCE_DEFAULT_RESERVATION_COUNT,
  )
  const [distribution, setDistribution] = useState<Sstv2EvidenceSeedPlan | null>(
    null,
  )
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    dispatch(thingsActions.clearSaList())
  }, [dispatch])

  // Remember only a well-formed id — a half-typed one must not clobber the value
  // the operator will want back on the next visit.
  useEffect(() => {
    if (!isSstv1LockerId(sourceLocker)) return
    try {
      window.localStorage.setItem(SOURCE_LOCKER_STORAGE_KEY, sourceLocker.trim())
    } catch {
      /* storage disabled — remembering is a convenience, never a precondition */
    }
  }, [sourceLocker])

  // Tenant picker source. The synthetic fixture tenant is offered as the
  // default (it is not a real tenant, so it never appears in SaGetTenants);
  // real tenants follow so an operator can materialize the migrated locker into
  // one of them.
  const tenantsQuery = useGetTenantsQuery({}, { skip: !isReady })
  const tenantOptions = useMemo(() => {
    const real = (
      (tenantsQuery.data?.things ?? []) as Array<{ id: string; name?: string }>
    )
      .filter((tn) => tn.id)
      .map((tn) => ({
        id: tn.id,
        label: tn.name ? `${tn.name} (${tn.id})` : tn.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return [
      {
        id: SSTV2_EVIDENCE_FIXTURE_TENANT,
        label: t("evidenceSyntheticTenantOption", {
          tenant: SSTV2_EVIDENCE_FIXTURE_TENANT,
        }),
      },
      ...real,
    ]
  }, [tenantsQuery.data, t])

  const runsQuery = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetSstv2EvidenceRuns,
        "ap.limit": 100,
      },
    },
    { skip: !isReady, pollingInterval: POLL_INTERVAL_MS, skipPollingIfUnfocused: true },
  )

  const runs = useAppSelector((s) =>
    selectSaList<Sstv2MigrationEvidenceRun>(s),
  )
    .filter((r) => r.thingTypeMrn === Sstv2MigrationEvidenceRunThingTypeMrn)
    .slice()
    .sort((a, b) => (b.startedAt || "").localeCompare(a.startedAt || ""))

  const latest = runs[0]
  const hasActive = !!latest && isActiveEvidenceRunStatus(latest.status)

  // Which run is shown in detail. Null = follow the latest (so live progress and
  // freshly-started runs surface automatically); clicking a history row pins one.
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const selected = runs.find((r) => r.runId === selectedRunId) ?? latest
  const viewingLatest = !!selected && !!latest && selected.runId === latest.runId

  const [startRun, startState] = useStartSstv2EvidenceRunMutation()
  const [cancelRun, cancelState] = useCancelSstv2EvidenceRunMutation()

  const onCancel = useCallback(async () => {
    setNotice(null)
    try {
      await cancelRun().unwrap()
      runsQuery.refetch()
    } catch (e: any) {
      setNotice(e?.data?.message || t("evidenceRunStartFailed"))
    }
  }, [cancelRun, runsQuery, t])

  const onRun = useCallback(async () => {
    setNotice(null)
    if (!isSstv1LockerId(sourceLocker)) {
      setNotice(t("evidenceSourceLockerInvalid"))
      return
    }
    try {
      const res = await startRun({
        sourceLocker: sourceLocker.trim(),
        reseed,
        runRollback,
        targetTenantId,
        reservationCount,
        ...(distribution ? { distribution } : {}),
      }).unwrap()
      if (!res.data.started) {
        if (res.data.reason === "run_in_progress") {
          setNotice(t("evidenceRunBusy", { id: res.data.activeRunId ?? "" }))
        } else {
          setNotice(res.data.error || t("evidenceRunStartFailed"))
        }
      } else {
        setSelectedRunId(null) // snap the detail view back to the new run
        runsQuery.refetch()
      }
    } catch (e: any) {
      // A rejected start (e.g. the 400 for a bad locker id) arrives here, and its
      // reason lives under `data.data.error` — the same envelope the happy path
      // uses. Fall back rather than showing "undefined".
      setNotice(
        e?.data?.data?.error || e?.data?.message || t("evidenceRunStartFailed"),
      )
    }
  }, [
    sourceLocker,
    reseed,
    runRollback,
    targetTenantId,
    reservationCount,
    distribution,
    startRun,
    t,
    runsQuery,
  ])

  const running = startState.isLoading || hasActive

  // Reseed summary for the selected run. `created` can be below `requested` when
  // the door pool ran dry or the partner refused — surface both, never just the
  // ask (per memory: report outcomes faithfully).
  const reseedChip = useMemo((): {
    label: string
    color: "default" | "error" | "warning"
  } => {
    const r = selected?.reseed
    if (!r) return { label: "", color: "default" }
    if (!r.done) return { label: t("evidenceReseedFailed"), color: "error" }
    const created = r.created ?? (r.active ?? 0) + (r.historical ?? 0)
    const requested = r.requested ?? created
    const short = created < requested
    return {
      label: `${t("evidenceReseedFirst")}: ${created}/${requested} · ${r.active ?? 0}A / ${r.historical ?? 0}H`,
      color: short ? "warning" : "default",
    }
  }, [selected?.reseed, t])

  const reseedDetail = useMemo(() => {
    const by = selected?.reseed?.byStatus
    if (!by) return null
    const parts = Object.entries(by)
      .filter(([, n]) => n > 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([s, n]) => `${n} ${s}`)
    return parts.length ? parts.join(" · ") : null
  }, [selected?.reseed])

  return (
    <Box data-ui="UI-SL2W-263">
      {/* The sl1 chip shows what the DISPLAYED run drove, not a constant. */}
      <Sstv2EvidenceSafetyBanner
        sourceLocker={selected?.sourceLocker}
        host={selected?.host}
        targetTenantId={targetTenantId}
      />

      {/* Run control */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Sstv2EvidenceRunControls
          disabled={running}
          running={running}
          canRun={isReady}
          onRun={onRun}
          cancelling={cancelState.isLoading}
          showCancel={hasActive}
          onCancel={onCancel}
          tenantOptions={tenantOptions}
          targetTenantId={targetTenantId}
          onTargetTenantIdChange={setTargetTenantId}
          sourceLocker={sourceLocker}
          onSourceLockerChange={setSourceLocker}
          reseed={reseed}
          onReseedChange={setReseed}
          runRollback={runRollback}
          onRunRollbackChange={setRunRollback}
          reservationCount={reservationCount}
          onReservationCountChange={setReservationCount}
          distribution={distribution}
          onDistributionChange={setDistribution}
        />
        {!runRollback && (
          <Typography
            sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 1 }}
          >
            {t("evidenceRunRollbackHint")}
          </Typography>
        )}
        {notice && (
          <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
      </Paper>

      {/* Selected run detail (defaults to latest; history rows pin an older one) */}
      {selected ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 700 }}>
              {viewingLatest ? t("evidenceLatestRun") : t("evidenceViewingRun")}
            </Typography>
            <Chip size="small" color={evidenceStatusColor(selected.status)} label={selected.status} />
            <Typography sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary" }}>
              {selected.runId.slice(0, 8)} · {selected.startedAt}
            </Typography>
            {selected.reseed && (
              <Chip
                size="small"
                variant="outlined"
                color={reseedChip.color}
                label={reseedChip.label}
              />
            )}
            {!viewingLatest && (
              <Button size="small" onClick={() => setSelectedRunId(null)}>
                {t("evidenceBackToLatest")}
              </Button>
            )}
          </Stack>

          {/* What the reseed actually landed — observed statuses, not the request. */}
          {reseedDetail && (
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                color: "text.secondary",
                mb: 1.5,
              }}
            >
              {reseedDetail}
            </Typography>
          )}
          {selected.reseed?.activeClampedTo !== undefined && (
            <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
              <Typography sx={{ fontSize: "0.78rem" }}>
                {t("evidenceActiveClamped", {
                  doors: selected.reseed.doorsAvailable ?? 0,
                  active: selected.reseed.activeClampedTo,
                })}
              </Typography>
            </Alert>
          )}

          <Sstv2EvidenceRunStepper steps={selected.steps} />

          {(selected.verdict || selected.status === "COMPLETED") && (
            <Box sx={{ mt: 2 }}>
              <Sstv2EvidenceRunResult run={selected} />
            </Box>
          )}
          {selected.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {selected.error}
            </Alert>
          )}
        </Paper>
      ) : (
        <Typography sx={{ color: "text.secondary", mb: 2 }}>
          {runsQuery.isLoading ? t("loading") : t("evidenceNoRuns")}
        </Typography>
      )}

      {runs.length > 1 && (
        <Sstv2EvidenceRunHistory
          runs={runs}
          latestRunId={latest?.runId}
          selectedRunId={selected?.runId}
          onSelectRun={setSelectedRunId}
        />
      )}
    </Box>
  )
}

export default Sstv2SyntheticMigrationTab
